import type { ChipProps } from "@mui/material/Chip";

const PIN_BASE = "https://maps.google.com/mapfiles/ms/icons/";

const MAP_PIN = {
  yellow: `${PIN_BASE}yellow-dot.png`,
  orange: `${PIN_BASE}orange-dot.png`,
  ltBlue: `${PIN_BASE}ltblue-dot.png`,
  blue: `${PIN_BASE}blue-dot.png`,
  purple: `${PIN_BASE}purple-dot.png`,
  green: `${PIN_BASE}green-dot.png`,
  red: `${PIN_BASE}red-dot.png`,
  grey: `${PIN_BASE}grey-dot.png`,
};

export const normalizeStatusValue = (value?: string): string =>
  typeof value === "string" ? value.trim().toLowerCase() : "";

type StatusColorConfig = {
  chip: ChipProps["color"];
  markerIcon: string;
};

const DEFAULT_ITEM_CONFIG: StatusColorConfig = {
  chip: "info",
  markerIcon: MAP_PIN.blue,
};

const DEFAULT_SHIPMENT_CONFIG: StatusColorConfig = {
  chip: "info",
  markerIcon: MAP_PIN.blue,
};

const ITEM_STATUS_COLOR_CONFIG: Record<string, StatusColorConfig> = {
  pending: { chip: "info", markerIcon: MAP_PIN.yellow },
  "in transit": { chip: "primary", markerIcon: MAP_PIN.blue },
  delivered: { chip: "success", markerIcon: MAP_PIN.green },
  damaged: { chip: "error", markerIcon: MAP_PIN.red },
  missing: { chip: "error", markerIcon: MAP_PIN.red },
  delayed: { chip: "warning", markerIcon: MAP_PIN.orange },
};

const ORDER_STATUS_COLOR_CONFIG: Record<string, StatusColorConfig> = {
  open: { chip: "warning", markerIcon: MAP_PIN.yellow },
  fulfilled: { chip: "success", markerIcon: MAP_PIN.green },
  cancelled: { chip: "default", markerIcon: MAP_PIN.grey },
  delayed: { chip: "error", markerIcon: MAP_PIN.red },
  pending: { chip: "info", markerIcon: MAP_PIN.ltBlue },
  partial: { chip: "info", markerIcon: MAP_PIN.purple },
};

const SHIPMENT_STATUS_COLOR_CONFIG: Record<string, StatusColorConfig> = {
  created: { chip: "warning", markerIcon: MAP_PIN.yellow },
  dispatched: { chip: "info", markerIcon: MAP_PIN.ltBlue },
  intransit: { chip: "primary", markerIcon: MAP_PIN.blue },
  arrived: { chip: "secondary", markerIcon: MAP_PIN.purple },
  completed: { chip: "success", markerIcon: MAP_PIN.green },
  delayed: { chip: "error", markerIcon: MAP_PIN.red },
};

const resolveConfig = (
  config: Record<string, StatusColorConfig>,
  status?: string,
  fallback: StatusColorConfig = DEFAULT_ITEM_CONFIG
): StatusColorConfig => config[normalizeStatusValue(status)] ?? fallback;

export const getItemStatusChipColor = (status?: string): ChipProps["color"] =>
  resolveConfig(ITEM_STATUS_COLOR_CONFIG, status, DEFAULT_ITEM_CONFIG).chip;

export const getItemStatusMarkerIcon = (status?: string): string =>
  resolveConfig(ITEM_STATUS_COLOR_CONFIG, status, DEFAULT_ITEM_CONFIG).markerIcon;

export const getOrderStatusChipColor = (status?: string): ChipProps["color"] =>
  resolveConfig(ORDER_STATUS_COLOR_CONFIG, status, {
    chip: "info",
    markerIcon: MAP_PIN.yellow,
  }).chip;

export const getOrderStatusMarkerIcon = (status?: string): string =>
  resolveConfig(ORDER_STATUS_COLOR_CONFIG, status, {
    chip: "info",
    markerIcon: MAP_PIN.yellow,
  }).markerIcon;

export const getShipmentStatusChipColor = (status?: string): ChipProps["color"] =>
  resolveConfig(SHIPMENT_STATUS_COLOR_CONFIG, status, DEFAULT_SHIPMENT_CONFIG).chip;

export const getShipmentStatusMarkerIcon = (status?: string): string =>
  resolveConfig(SHIPMENT_STATUS_COLOR_CONFIG, status, DEFAULT_SHIPMENT_CONFIG).markerIcon;
